'use client'

import { useEffect, createContext } from 'react'

// Helper function to get the current subdomain
const getCurrentSubdomain = (): string | null => {
  if (typeof window === 'undefined') return null

  const hostname = window.location.hostname
  const parts = hostname.split('.')

  // Check if we have a subdomain (more than 2 parts)
  if (parts.length > 2) {
    return parts[0]
  }

  return null
}

// Helper function to get the appropriate API key based on subdomain
const getAmplitudeApiKey = (): string | undefined => {
  const subdomain = getCurrentSubdomain()

  // Use specific API key for kannact subdomain
  if (subdomain === 'kannact') {
    return process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY_KANNACT
  }

  // Use default API key for all other subdomains
  return process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY
}

const NODE_ENV = process.env.NEXT_PUBLIC_NODE_ENV || process.env.NODE_ENV
const SAMPLE_RATE = process.env.NEXT_PUBLIC_AMPLITUDE_SAMPLE_RATE || '1.0'
// Amplitude - Check if the environment is production or development
// TODO - Remove development environment before we push it to production
// || NODE_ENV === 'development'
const isProd = NODE_ENV === 'production' || NODE_ENV === 'development'

interface AmplitudeContextType {
  trackAmplitudeEvent: (
    eventName: string,
    eventProperties: Record<string, any>
  ) => void
}

export const AmplitudeContext = createContext<AmplitudeContextType>({
  trackAmplitudeEvent: () => {},
})

const AmplitudeContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  useEffect(() => {
    // Amplitude - Check if the environment is production or development
    // TODO - Remove development environment before we push it to production
    // console.log('Current environment:', NODE_ENV)
    // console.log('Is production?', isProd)

    const initAmplitude = async () => {
      try {
        const AMPLITUDE_API_KEY = getAmplitudeApiKey()

        if (isProd && AMPLITUDE_API_KEY && typeof window !== 'undefined') {
          const amplitude = await import('@amplitude/analytics-browser')
          const { sessionReplayPlugin } = await import(
            '@amplitude/plugin-session-replay-browser'
          )
          const { autocapturePlugin } = await import(
            '@amplitude/plugin-autocapture-browser'
          )

          // Create and install Session Replay Plugin
          const sessionReplayTracking = sessionReplayPlugin({
            sampleRate: Number(SAMPLE_RATE),
          })
          amplitude.add(sessionReplayTracking)

          // Create and install Autocapture Plugin
          const autocaptureTracking = autocapturePlugin()
          amplitude.add(autocaptureTracking)

          // Initialize Amplitude with session tracking and autocapture
          amplitude.init(AMPLITUDE_API_KEY, undefined, {
            defaultTracking: {
              sessions: true,
            },
            // Autocapture is enabled by default when the plugin is added
          })
        }
      } catch (error) {
        console.error('Failed to initialize Amplitude:', error)
      }
    }

    initAmplitude()
  }, [])

  const trackAmplitudeEvent = async (
    eventName: string,
    eventProperties: Record<string, any>
  ) => {
    try {
      if (isProd && typeof window !== 'undefined') {
        const amplitude = await import('@amplitude/analytics-browser')
        amplitude.track(eventName, eventProperties)
      }
    } catch (error) {
      console.error('Failed to track Amplitude event:', error)
    }
  }

  const value = { trackAmplitudeEvent }

  return (
    <AmplitudeContext.Provider value={value}>
      {children}
    </AmplitudeContext.Provider>
  )
}

export default AmplitudeContextProvider
